import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { KeyRound, ArrowRight, ShieldCheck } from 'lucide-react';

export default function TrocarSenha() {
  const navigate = useNavigate();
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [busy, setBusy] = useState(false);

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current || !novaSenha) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (novaSenha.length < 6) {
      toast.error('Nova senha deve ter no mínimo 6 caracteres');
      return;
    }
    if (novaSenha !== confirma) {
      toast.error('A confirmação não bate');
      return;
    }
    setBusy(true);
    const result = await changePassword(current, novaSenha);
    setBusy(false);
    if (result.success) {
      toast.success('Senha alterada com sucesso!');
      if (user.role === 'vendedor') navigate('/vendedor/dashboard');
      else if (user.role === 'cliente') navigate('/cliente/dashboard');
      else navigate('/dashboardadminromaneio');
    } else {
      toast.error(result.message || 'Erro ao alterar senha');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 text-white text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-3">
            <KeyRound className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold">Crie sua nova senha</h1>
          <p className="text-sm opacity-90 mt-1">
            {user.must_change_password
              ? 'Primeiro acesso — crie uma senha pessoal'
              : 'Defina uma nova senha'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 dark:text-blue-200">Olá, {user.name}!</p>
              <p className="text-blue-700 dark:text-blue-300 mt-0.5">
                Por segurança, troque a senha que recebeu por e-mail. Use uma combinação fácil de lembrar.
              </p>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5 block">
              Senha atual (a que veio por e-mail)
            </label>
            <input
              type="password"
              value={current}
              onChange={e => setCurrent(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="trocar-senha-atual"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5 block">
              Nova senha (mín. 6 caracteres)
            </label>
            <input
              type="password"
              value={novaSenha}
              onChange={e => setNovaSenha(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="trocar-senha-nova"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5 block">
              Confirme a nova senha
            </label>
            <input
              type="password"
              value={confirma}
              onChange={e => setConfirma(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 ${confirma && novaSenha !== confirma ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-orange-500'}`}
              data-testid="trocar-senha-confirma"
            />
            {confirma && novaSenha !== confirma && (
              <p className="text-xs text-red-600 mt-1">As senhas não conferem</p>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="trocar-senha-submit"
          >
            {busy ? 'Salvando...' : 'Salvar nova senha'}
            <ArrowRight className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={async () => { await logout(); navigate('/login'); }}
            className="w-full text-xs text-gray-500 hover:text-gray-700 mt-2"
          >
            Sair
          </button>
        </form>
      </div>
    </div>
  );
}
